import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { EUserRole } from 'enums';
import { IUser } from 'models';
import {
  useUpdateTeamMemberMutation,
  useUpdateUserRoleMutation,
  useAddProfileImageMutation,
  // useUpdateUserMutation,
  useUsers,
} from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';

interface UseUserSettingsFormManagerProps {
  userId: number;
  next?: (data?: any) => void;
}

export const useUserSettingsFormManager = ({ userId, next = () => {} }: UseUserSettingsFormManagerProps) => {
  const { orgSlug, id: currentUserId } = useAppSelector(selectCurrentUserInfo);
  const { data: users = [], isLoading: loading_form } = useUsers();
  const user = users.find((user: any) => user.id === userId) as IUser;
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);

  // TODO: The API of updating auth user doesn't update code, so use only the API of updating user by id for now.
  // const useMutation = userId === currentUserId ? useUpdateUserMutation : useUpdateTeamMemberMutation;
  const useMutation = useUpdateTeamMemberMutation;

  const [
    updateUser,
    { isLoading: loading_updateUser, isSuccess: success_updateUser, isError: error_updateUser, ...rest },
  ] = useMutation();

  const [
    updateUserRole,
    { isLoading: loading_updateUserRole, isSuccess: success_updateUserRole, isError: error_updateUserRole },
  ] = useUpdateUserRoleMutation();

  const [
    addProfileImage,
    { isLoading: loading_profileImage, isSuccess: success_profileImage, isError: error_profileImage },
  ] = useAddProfileImageMutation();

  const initialValues = {
    avatar: null as File | null,
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone?.slice(-10) || '',
    code: user?.code || '',
    role: user && user?.roles?.length > 0 ? user?.roles[0]?.name || EUserRole.NO_ACCESS : EUserRole.NO_ACCESS,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Your name is required'),
    email: Yup.string().email().required('Email address is required'),
    phone: Yup.string()
      .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'phone number must be a valid 10 digit phone number')
      .required('Phone number is required'),
    code: Yup.string().nullable(),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { avatar, ...rest } = values;

      setLoadingSubmit(true);

      if (avatar) {
        const bodyFormData = new FormData();
        bodyFormData.append('id', `${user?.id}`);
        bodyFormData.append('profile_photo', avatar);

        addProfileImage({ data: bodyFormData });
      } else {
        let mutateProps: any = {
          user: {
            ...user,
            ...rest,
          },
        };

        // if (userId !== currentUserId) {
        //   mutateProps = { ...mutateProps, orgSlug, userId };
        // }

        mutateProps = { ...mutateProps, orgSlug, userId };

        updateUser(mutateProps);
      }
    },
  });

  useEffect(() => {
    if (error_profileImage) {
      setLoadingSubmit(false);
    }

    if (!loading_profileImage && success_profileImage && !error_profileImage) {
      const { avatar, ...rest } = formManager.values; // eslint-disable-line

      let mutateProps: any = {
        user: { ...user, ...rest },
      };

      // if (userId !== currentUserId) {
      //   mutateProps = { ...mutateProps, orgSlug, userId };
      // }

      mutateProps = { ...mutateProps, orgSlug, userId };

      updateUser(mutateProps);
    }
  }, [loading_profileImage, success_profileImage, error_profileImage]);

  useEffect(() => {
    if (error_updateUser) {
      setLoadingSubmit(false);
    }

    if (!loading_updateUser && success_updateUser && !error_updateUser) {
      if (userId !== currentUserId) {
        updateUserRole({ orgSlug, userId, role: formManager.values.role as any });
      } else {
        const { data } = rest;

        next(data);

        toast.success('The user profile has been successfully updated.');

        setLoadingSubmit(false);
      }
    }
  }, [loading_updateUser, success_updateUser, error_updateUser]);

  useEffect(() => {
    if (error_updateUserRole) {
      setLoadingSubmit(false);
    }

    if (!loading_updateUserRole && success_updateUserRole && !error_updateUserRole) {
      const { data } = rest;

      next(data);

      setLoadingSubmit(false);
    }
  }, [loading_updateUserRole, success_updateUserRole, error_updateUserRole]);

  return [{ user, formManager, loading_form, loading_submit }] as [
    { user: IUser; formManager: FormikProps<typeof initialValues>; loading_form: boolean; loading_submit: boolean },
  ];
};
